import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { PlanSelector, plans } from './components/PlanSelector';

const ghlLinks: Record<string, string> = {
  hatchling: 'https://go.dino-nuggets.com/checkout-hatchling',
  nuggets: 'https://go.dino-nuggets.com/checkout-nuggets',
  titan: 'https://go.dino-nuggets.com/checkout-titan',
};

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan') || 'nuggets';
  const addonParam = searchParams.get('addon') === 'business-launch';
  const onlineStoreParam = searchParams.get('addon') === 'online-store';
  const [selectedPlan, setSelectedPlan] = useState(planParam);
  const [addOnSelected, setAddOnSelected] = useState(addonParam);
  const [onlineStoreSelected, setOnlineStoreSelected] = useState(onlineStoreParam);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentPlan = plans.find((p) => p.id === selectedPlan) || plans[1];
  const planPrice = parseInt(currentPlan.price.replace('$', ''), 10);
  const addOnTotal = (addOnSelected ? 350 : 0) + (onlineStoreSelected ? 997 : 0);
  const totalLabel = addOnTotal > 0
    ? `$${planPrice}/mo + $${addOnTotal} one-time`
    : `${currentPlan.price}/mo`;

  const handleGetStarted = () => {
    const url = ghlLinks[selectedPlan] || ghlLinks.nuggets;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="text-primary font-extrabold text-xl tracking-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Dino Nuggets
            </span>
          </Link>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-lock-line text-sm"></i>
            </div>
            <span>Secure Checkout</span>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-8">
          <Link to="/" className="hover:text-primary transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line text-base"></i>
          <span className="text-secondary font-medium">Checkout</span>
        </div>

        <div className="text-center mb-10">
          <h1 className="font-display text-3xl font-bold text-secondary mb-2">Choose Your Growth Plan</h1>
          <p className="text-gray-500 text-sm max-w-lg mx-auto">Pick the setup that fits your business, then click the button below to continue securely.</p>
        </div>

        <div className="max-w-2xl mx-auto">
          <PlanSelector
            selectedPlan={selectedPlan}
            onSelectPlan={setSelectedPlan}
            addOnSelected={addOnSelected}
            onToggleAddOn={setAddOnSelected}
            onlineStoreSelected={onlineStoreSelected}
            onToggleOnlineStore={setOnlineStoreSelected}
          />

          {/* Order Summary */}
          {(addOnSelected || onlineStoreSelected) && (
            <div className="mt-6 bg-secondary/5 border border-secondary/20 rounded-xl px-6 py-4">
              <h4 className="font-semibold text-secondary text-sm mb-3">Order Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{currentPlan.name} (monthly)</span>
                  <span className="font-medium text-secondary">{currentPlan.price}/mo</span>
                </div>
                {addOnSelected && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Business Launch Package (one-time)</span>
                    <span className="font-medium text-secondary">$997</span>
                  </div>
                )}
                {onlineStoreSelected && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Online Store Setup (one-time)</span>
                    <span className="font-medium text-secondary">$997</span>
                  </div>
                )}
                <div className="border-t border-secondary/20 pt-2 flex justify-between font-bold">
                  <span className="text-secondary">Today&apos;s Total</span>
                  <span className="text-secondary">${planPrice + addOnTotal}</span>
                </div>
                <p className="text-xs text-gray-400 pt-1">Then {currentPlan.price}/month starting next billing cycle.</p>
              </div>
            </div>
          )}

          {/* CTA Button */}
          <div className="mt-8">
            <button
              type="button"
              onClick={handleGetStarted}
              className="w-full bg-primary hover:bg-primary/90 text-white py-4 rounded-full font-semibold text-base transition-all duration-200 hover:scale-[1.02] hover:shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              Continue to Checkout — {totalLabel}
              <i className="ri-external-link-line text-lg"></i>
            </button>
            <p className="text-center text-xs text-gray-400 mt-3">You'll be redirected to our secure order form to complete payment.</p>
          </div>

          {/* Trust badges */}
          <div className="mt-10 grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-primary/10 flex items-center justify-center">
                <i className="ri-shield-check-line text-primary text-lg"></i>
              </div>
              <span className="text-[11px] text-gray-500 leading-tight block">No Long-Term Contracts</span>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-primary/10 flex items-center justify-center">
                <i className="ri-flashlight-line text-primary text-lg"></i>
              </div>
              <span className="text-[11px] text-gray-500 leading-tight block">Same-Day Setup</span>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-primary/10 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-primary text-lg"></i>
              </div>
              <span className="text-[11px] text-gray-500 leading-tight block">Dedicated Support</span>
            </div>
          </div>

          {/* What happens next */}
          <div className="mt-10 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-display font-semibold text-secondary text-sm mb-4">What Happens After You Pick a Plan?</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">1</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary">Quick Onboarding Call</p>
                  <p className="text-xs text-gray-500">We will schedule a short call to learn about your business and goals.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">2</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary">Custom Setup</p>
                  <p className="text-xs text-gray-500">Our team builds and configures the plan you selected.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">3</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary">Go Live</p>
                  <p className="text-xs text-gray-500">Your new system is live and working for your business.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Minimal footer */}
      <footer className="border-t border-gray-100 mt-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-400">&copy; 2025 Dino Nuggets. All rights reserved.</p>
          <div className="flex gap-4 text-xs">
            <a href="/privacy-policy" className="text-gray-400 hover:text-primary transition-colors cursor-pointer">Privacy Policy</a>
            <a href="/terms-of-service" className="text-gray-400 hover:text-primary transition-colors cursor-pointer">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
